'use client'

import { useAtom, useStore } from 'jotai'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useRef } from 'react'
import type { UseFormReturn } from 'react-hook-form'

import type { UnifiedOrderFormValues } from '@/features/order/order-form/lib/unified-order-schema'
import { getExchangeLabel } from '@/lib/configs/exchange-registry'
import { toExchangeEnum, tradeVenueToExchangeKey } from '@/lib/proto/util'
import { toast } from '@/lib/toast'
import { useUserAccounts } from '@/service/account.hook'
import { useConnectedVenueIssues } from '@/service/venue-trading-status.hook'
import {
  optimisticOrderResultAtomFamily,
  type OptimisticOrderTarget,
  type PendingOptimisticOrder,
  pendingOptimisticOrdersAtom,
  pendingOptimisticSymbolsAtom,
  targetKey,
} from '@/state/order'
import type { SelectedInstrument, SelectedOption } from '@/types/dashboard'

// A venue normally answers a submit (accepted / filled / rejected) within about a second. Warn
// soon after: in an outage the silence reads as "it didn't go through", and an order resent
// then can execute twice once the venue recovers.
const NO_VENUE_RESPONSE_WARN_MS = 2_000

export function useOptimisticOrderClear(
  cardId: string,
  form: UseFormReturn<UnifiedOrderFormValues>,
  setSelectedInstruments: (
    upd: SelectedInstrument[] | ((prev: SelectedInstrument[]) => SelectedInstrument[])
  ) => void,
  setSelectedOptions: (
    upd: SelectedOption[] | ((prev: SelectedOption[]) => SelectedOption[])
  ) => void
) {
  const store = useStore()
  const [, setPendingOrders] = useAtom(pendingOptimisticOrdersAtom)
  const [, setPendingSymbols] = useAtom(pendingOptimisticSymbolsAtom)
  const [signal, setSignal] = useAtom(optimisticOrderResultAtomFamily(cardId))
  const t = useTranslations('orderForm')
  const { data: accountsData } = useUserAccounts()
  const accountsRef = useRef(accountsData?.accounts)
  accountsRef.current = accountsData?.accounts
  // The connection status's own venue-health read (same streams): lets a submit into a venue
  // that's already known to be down say so at once.
  const venueIssues = useConnectedVenueIssues()
  const venueIssuesRef = useRef(venueIssues)
  venueIssuesRef.current = venueIssues
  const lastDownVenueWarnAtRef = useRef(0)

  /**
   * Called at the click, for every submit mode (doesn't block the submit). Warns when an
   * earlier order for the same account + symbol is still unanswered — resending in an outage
   * is how orders double. An order to a venue already known to be down needs no toast: the
   * submit button itself reads "<venue> offline"; this only keeps the no-response fallback
   * from repeating it.
   */
  const warnIfVenueDown = useCallback(
    (targets: readonly OptimisticOrderTarget[]) => {
      // Resending while an earlier order for the same account + symbol is still unanswered:
      // that one may yet execute, and both would fill once the venue answers.
      const pendingOrders = store.get(pendingOptimisticOrdersAtom)
      const unanswered = targets.find((target) => {
        const key = targetKey(target.accountId, target.symbol)
        return (store.get(pendingOptimisticSymbolsAtom)[key] ?? []).some((id) => {
          const pending = pendingOrders[id]
          return pending && !pending.acknowledged && !pending.resolved[key]
        })
      })
      if (unanswered) {
        toast.warning(t('unansweredOrderPending', { symbol: unanswered.symbol }), {
          symbol: unanswered.symbol,
          position: 'bottom-right',
        })
      }

      for (const target of targets) {
        const account = accountsRef.current?.find((a) => a.accountId === target.accountId)
        if (!account) continue
        const exchange = toExchangeEnum(tradeVenueToExchangeKey(account.tradeVenue))
        if (!venueIssuesRef.current.some((issue) => issue.exchange === exchange)) continue
        // The submit button already says this venue is offline; just keep the no-response
        // fallback below from repeating it.
        lastDownVenueWarnAtRef.current = Date.now()
        return true
      }
      return false
    },
    [store, t]
  )

  // Prune pending orders that never received a signal (e.g. network failure, deleted widget).
  // Runs once on mount and sets up a 5-minute sweep interval.
  const sweepRef = useRef<ReturnType<typeof setInterval> | null>(null)
  useEffect(() => {
    const PENDING_TTL_MS = 5 * 60 * 1000

    const sweep = () => {
      const now = Date.now()
      setPendingOrders((prev) => {
        const stale = Object.keys(prev).filter((id) => now - prev[id].submittedAt > PENDING_TTL_MS)
        if (stale.length === 0) return prev
        const next = { ...prev }
        const staleKeys = new Set(
          stale.flatMap((id) => prev[id].targets.map((t) => targetKey(t.accountId, t.symbol)))
        )
        stale.forEach((id) => {
          if (typeof window !== 'undefined') localStorage.removeItem(prev[id].snapshotKey)
          delete next[id]
        })
        setPendingSymbols((prevSym) => {
          const nextSym = { ...prevSym }
          staleKeys.forEach((key) => {
            nextSym[key] = (nextSym[key] ?? []).filter((id) => !stale.includes(id))
            if (nextSym[key].length === 0) delete nextSym[key]
          })
          return nextSym
        })
        return next
      })
    }

    sweep()
    sweepRef.current = setInterval(sweep, PENDING_TTL_MS)
    return () => {
      if (sweepRef.current) clearInterval(sweepRef.current)
    }
  }, [setPendingOrders, setPendingSymbols])

  const triggerOptimistic = useCallback(
    (
      submissionId: string,
      targets: OptimisticOrderTarget[],
      instruments: SelectedInstrument[],
      options: SelectedOption[]
    ) => {
      const snapshotKey = `order-optimistic-snapshot-${cardId}-${submissionId}`
      const submittedAt = Date.now()

      localStorage.setItem(
        snapshotKey,
        JSON.stringify({ instruments, options, formValues: form.getValues(), targets, submittedAt })
      )

      const entry: PendingOptimisticOrder = {
        submissionId,
        cardId,
        targets,
        resolved: {},
        snapshotKey,
        submittedAt,
      }
      setPendingOrders((prev) => ({ ...prev, [submissionId]: entry }))

      setPendingSymbols((prev) => {
        const next = { ...prev }
        targets.forEach(({ accountId, symbol }) => {
          const key = targetKey(accountId, symbol)
          next[key] = [...(next[key] ?? []), submissionId]
        })
        return next
      })

      // The ticket clears for the next order — fully: what another widget (a chart tab) is
      // showing never keeps a row in the form.
      setSelectedInstruments(() => [])
      setSelectedOptions(() => [])

      // A submit has no success toast — the venue's fill/reject notification is the answer.
      // When the venue is down it never comes — not even a rejection — and the order went
      // quiet (the 5-minute sweep above drops it silently). warnIfVenueDown speaks up at the
      // click when the venue is already known to be down; this catches the rest once an answer
      // is clearly overdue. The order stays pending (it may still go through, so the form
      // isn't restored). Not cancelled on unmount: the Order Form popup usually closes right
      // after submitting.
      window.setTimeout(() => {
        // Already told at the click that this venue is down.
        if (Date.now() - lastDownVenueWarnAtRef.current < NO_VENUE_RESPONSE_WARN_MS * 2) return
        const pending = store.get(pendingOptimisticOrdersAtom)[submissionId]
        // Gone (resolved) or answered (e.g. a TWAP that's running): the venue isn't silent.
        if (!pending || pending.acknowledged) return
        const unanswered = pending.targets.find(
          (target) => !pending.resolved[targetKey(target.accountId, target.symbol)]
        )
        if (!unanswered) return
        const account = accountsRef.current?.find((a) => a.accountId === unanswered.accountId)
        const venue = account
          ? getExchangeLabel(toExchangeEnum(tradeVenueToExchangeKey(account.tradeVenue)))
          : t('noVenueResponseVenueFallback')
        toast.warning(t('noVenueResponse', { venue, symbol: unanswered.symbol }), {
          symbol: unanswered.symbol,
          position: 'bottom-right',
        })
      }, NO_VENUE_RESPONSE_WARN_MS)
    },
    [
      cardId,
      form,
      setPendingOrders,
      setPendingSymbols,
      setSelectedInstruments,
      setSelectedOptions,
      store,
      t,
    ]
  )

  useEffect(() => {
    if (!signal) return
    setSignal(null)

    const { submissionId, result } = signal
    const pending = store.get(pendingOptimisticOrdersAtom)[submissionId]
    if (!pending) return

    if (result === 'failure') {
      const saved = localStorage.getItem(pending.snapshotKey)
      if (saved) {
        try {
          const snap = JSON.parse(saved)
          setSelectedInstruments(() => snap.instruments)
          setSelectedOptions(() => snap.options)
          form.reset(snap.formValues)
        } catch (e) {
          console.warn('[optimistic] snapshot restore failed', e)
        }
      }
    }

    localStorage.removeItem(pending.snapshotKey)

    setPendingOrders((prev) => {
      const next = { ...prev }
      delete next[submissionId]
      return next
    })

    setPendingSymbols((prev) => {
      const next = { ...prev }
      pending.targets.forEach(({ accountId, symbol }) => {
        const key = targetKey(accountId, symbol)
        next[key] = (next[key] ?? []).filter((id) => id !== submissionId)
        if (next[key].length === 0) delete next[key]
      })
      return next
    })
  }, [
    signal,
    store,
    form,
    setSignal,
    setPendingOrders,
    setPendingSymbols,
    setSelectedInstruments,
    setSelectedOptions,
  ])

  return { triggerOptimistic, warnIfVenueDown }
}
